import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { SiblingAppId } from "@/lib/sibling-apps";

export type CrisisResource = {
  id: string;
  app: string;
  country: string | null;
  category: string | null;
  name: string;
  phone: string | null;
  sms: string | null;
  url: string | null;
  hours: string | null;
  languages: string[] | null;
  notes: string | null;
  display_order: number;
  active: boolean;
};

type UseCrisisResourcesOptions = {
  app: SiblingAppId | null;
};

type UseCrisisResourcesResult = {
  global: CrisisResource[];
  community: CrisisResource[];
  loading: boolean;
  error: Error | null;
};

export function useCrisisResources({
  app,
}: UseCrisisResourcesOptions): UseCrisisResourcesResult {
  const [global, setGlobal] = useState<CrisisResource[]>([]);
  const [community, setCommunity] = useState<CrisisResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        // Fetch global lines + current sibling app's lines in one query.
        const apps = app ? ["global", app] : ["global"];

        const { data, error: queryError } = await (supabase as any)
          .from("crisis_resources")
          .select("*")
          .in("app", apps)
          .eq("active", true)
          .order("display_order", { ascending: true });

        if (queryError) throw queryError;
        if (cancelled) return;

        const rows = (data ?? []) as CrisisResource[];
        setGlobal(rows.filter((r) => r.app === "global"));
        setCommunity(rows.filter((r) => r.app !== "global"));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [app]);

  return { global, community, loading, error };
}
